import * as Sentry from "@sentry/react";
import ReactDOM from "react-dom/client";
import styles from "./assets/stylesheets/embed.css?url";
import I18n, { DEFAULT_LOCALE, LOCALES } from "./locales";
import { FLOW_KEYS } from "./flows";
import { FlowSurvey } from "./views/flow_survey";
import { apiClient } from "./client";
import { LocalizationWrapper } from "./components/localization_wrapper";

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || "production",
    integrations: [
      Sentry.thirdPartyErrorFilterIntegration({
        filterKeys: ["tree-herent-mobility-2026"],
        behaviour: "drop-error-if-exclusively-contains-third-party-frames",
      }),
    ],
  });
}

class HerentMobilityEmbed extends HTMLElement {
  static tagName = "herent-mobility-embed";

  static register(tagName = this.tagName, registry) {
    if ("customElements" in window) {
      (registry || customElements).define(tagName, this);
    }
  }

  #tellitScript = null;

  connectedCallback() {
    if (!FLOW_KEYS.includes(this.dataset.flow)) {
      throw new Error("data-flow is required and must match a configured flow");
    }

    this.#insertTellitScript();
    window.addEventListener("resize", this.setElementHeight);
    window.addEventListener("blur", this.setElementHeight);
    this.setElementHeight();
    if (!this.shadow) {
      this.shadow = this.attachShadow({ mode: "closed" });
      this.reactRoot = ReactDOM.createRoot(this.shadow);
    }
    this.renderElement();
  }

  disconnectedCallback() {
    window.removeEventListener("resize", this.setElementHeight);
    window.removeEventListener("blur", this.setElementHeight);
    this.#tellitScript?.remove();
  }

  setElementHeight = () => {
    this.style.setProperty("--app-height", `${window.innerHeight}px`);
  };

  renderElement() {
    this.reactRoot.render(
      <LocalizationWrapper locale={this.i18n.locale}>
        <div id="herent-mobility-root" data-flow={this.dataset.flow}>
          <style>
            {":where(#herent-mobility-root) { visibility: hidden; }"}
          </style>
          <link rel="stylesheet" href={this.stylesheetURL} />
          <FlowSurvey
            apiClient={apiClient}
            flow={this.dataset.flow}
            initialStreet={this.dataset.street}
          />
        </div>
      </LocalizationWrapper>,
    );
  }

  get i18n() {
    const locale = this.lang || document.documentElement.lang || DEFAULT_LOCALE;
    const normalizedLocale = locale.toLowerCase().split("-")[0];
    return new I18n(
      LOCALES.includes(normalizedLocale) ? normalizedLocale : DEFAULT_LOCALE,
    );
  }

  get stylesheetURL() {
    return styles;
  }

  #insertTellitScript() {
    this.#tellitScript = document.createElement("script");
    this.#tellitScript.type = "module";
    this.#tellitScript.src =
      "https://tree-projects.s3.eu-central-1.amazonaws.com/tellit/embed.js";
    this.before(this.#tellitScript);
  }
}

HerentMobilityEmbed.register();
